import {
  AlertTriangle, ArrowRight, CheckCircle2, CircleDot, ExternalLink, FolderLock, Inbox, KeyRound, Link2, Mail,
  Monitor, Plus, Printer, Radar, ShieldAlert, Smartphone, Ticket as TicketIcoon, UserRound, Vault, Wifi, Zap,
} from 'lucide-react'
import type { Aandacht, Bewaking, Link as LinkT } from '../../gedeeld/types'
import { api } from '../lib/api'
import { useLaad } from '../lib/laden'
import { geleden, kaalAdres, termijn } from '../lib/opmaak'
import { Link, navigeer, useTitel } from '../lib/router'
import { meldProbleem, useStaat } from '../lib/staat'
import { geheimSoort, itemKenmerk, itemSoort, niveau } from '../lib/woorden'
import { Code } from './kluis/Code'
import { Tegel } from './stukjes'
import { TicketLijst } from './tickets/Tickets'
import { Badge, Foutvlak, Knop, Laden } from './ui'

/* ===========================================================================
 *  Het overzicht
 *
 *  Voor iedereen hetzelfde begin: een probleem melden is één klik, en
 *  daaronder wat van jou is -- je tickets, je codes, je spullen, je links
 *  en waar je bij kunt. Voor IT staat daarboven wat er ligt: open tickets,
 *  en wat binnenkort aandacht vraagt (garantie, APK, een code die gewijzigd
 *  moet worden, tijdelijke toegang die is verlopen).
 * =========================================================================== */

const SNEL = [
  { categorie: 'printer', label: 'Printer', icoon: <Printer size={16} /> },
  { categorie: 'netwerk', label: 'Internet of wifi', icoon: <Wifi size={16} /> },
  { categorie: 'account', label: 'Inloggen', icoon: <KeyRound size={16} /> },
  { categorie: 'hardware', label: 'Laptop of pc', icoon: <Monitor size={16} /> },
  { categorie: 'telefoon', label: 'Telefoon of iPad', icoon: <Smartphone size={16} /> },
  { categorie: 'mail', label: 'Mail', icoon: <Mail size={16} /> },
  { categorie: 'beveiliging', label: 'Verdachte mail of kwijt', icoon: <ShieldAlert size={16} /> },
]

function groet(): string {
  const u = new Date().getHours()
  return u < 6 ? 'Goedenacht' : u < 12 ? 'Goedemorgen' : u < 18 ? 'Goedemiddag' : 'Goedenavond'
}

export default function Start() {
  const ik = useStaat((s) => s.ik)!
  const beheerder = useStaat((s) => s.beheerder)
  const { data, fout, laden, opnieuw } = useLaad(() => api.start(), [])
  useTitel('Overzicht', beheerder ? 'IT van het hele bedrijf' : 'Wat van jou is')

  const voornaam = ik.naam.split(' ')[0] ?? ik.naam

  return (
    <div className="pagina">
      <div className="pagina-kop">
        <div>
          <h1>{groet()}, {voornaam}</h1>
          <p>{beheerder ? 'Wat er bij IT ligt, en wat van jou is.' : 'Je spullen, je codes, je tickets en waar je bij kunt.'}</p>
        </div>
      </div>

      {fout && <Foutvlak fout={fout} opnieuw={opnieuw} />}
      {laden && !data && <Laden />}

      {data?.beheer && (
        <>
          <div className="tegels zes">
            <Tegel label="Open" getal={data.beheer.telling.open} icoon={<Inbox size={20} />} naar="/tickets" />
            <Tegel label="Te laat" getal={data.beheer.telling.teLaat} icoon={<CircleDot size={20} />} kleur="danger" naar="/tickets"
              gevaar={data.beheer.telling.teLaat > 0} sub={`${data.beheer.telling.nieuw} nieuw`} />
            <Tegel label="Nog bij niemand" getal={data.beheer.telling.nietToegewezen} icoon={<UserRound size={20} />} kleur="oranje"
              naar="/tickets?f=niet_toegewezen" gevaar={data.beheer.telling.nietToegewezen > 0} />
            <Tegel label="Spoed" getal={data.beheer.telling.spoed} icoon={<Zap size={20} />} kleur="danger" naar="/tickets" gevaar={data.beheer.telling.spoed > 0} />
            <Tegel label="Voor mij" getal={data.beheer.telling.voorMij} icoon={<TicketIcoon size={20} />} kleur="paars" naar="/tickets?f=voor_mij" />
            <Tegel label="Opgelost, 7 dagen" getal={data.beheer.telling.opgelostWeek} icoon={<CheckCircle2 size={20} />} kleur="ok" naar="/tickets?f=afgerond" />
          </div>

          <div className="twee-kolom start-beheer">
            <section className="vlak">
              <div className="vlak-kop">
                <h2><TicketIcoon size={15} /> Open tickets</h2>
                <Link naar="/tickets" className="vlak-link">Alle tickets <ArrowRight size={12} /></Link>
              </div>
              {data.beheer.open.length
                ? <TicketLijst tickets={data.beheer.open} beheerder compact />
                : <p className="stil">Er ligt niets. Mooi.</p>}
            </section>
            <section className="vlak">
              <div className="vlak-kop">
                <h2><AlertTriangle size={15} /> Vraagt aandacht</h2>
              </div>
              {data.beheer.aandacht.length
                ? <AandachtLijst regels={data.beheer.aandacht} />
                : <p className="stil">Geen garantie, APK, lease of code die binnenkort iets vraagt.</p>}
            </section>
          </div>
        </>
      )}

      {data?.beheer && data.beheer.bewaking.length > 0 && <BewakingVlak regels={data.beheer.bewaking} />}

      <section className="vlak melden-vlak">
        <div>
          <h2>Doet er iets het niet, of heb je iets nodig?</h2>
          <p className="stil">Meld het hier. IT krijgt meteen een seintje, en jij ziet hoe ver het is.</p>
        </div>
        <div className="snel-melden">
          {SNEL.map((s) => (
            <button key={s.categorie} type="button" className="snel-knop" onClick={() => meldProbleem({ categorie: s.categorie })}>
              {s.icoon}<span>{s.label}</span>
            </button>
          ))}
          <Knop soort="hoofd" icoon={<Plus size={16} />} onClick={() => meldProbleem()}>Iets anders</Knop>
        </div>
      </section>

      {data && (
        <div className="twee-kolom">
          <section className="vlak">
            <div className="vlak-kop">
              <h2><TicketIcoon size={15} /> Mijn tickets</h2>
              <Link naar={beheerder ? '/tickets?f=alles' : '/tickets'} className="vlak-link">Alles <ArrowRight size={12} /></Link>
            </div>
            {data.mijnTickets.length
              ? <TicketLijst tickets={data.mijnTickets.slice(0, 8)} beheerder={false} compact />
              : <p className="stil">Je hebt nog niets gemeld.</p>}
          </section>

          <section className="vlak">
            <div className="vlak-kop">
              <h2><Vault size={15} /> {beheerder ? 'Met mij gedeeld' : 'Mijn codes'}</h2>
              <Link naar="/kluis" className="vlak-link">{beheerder ? 'Naar de kluis' : 'Alle codes'} <ArrowRight size={12} /></Link>
            </div>
            {data.codes.length ? (
              <div className="mini-kluis">
                {data.codes.slice(0, 10).map((g) => {
                  const S = geheimSoort(g.soort)
                  return (
                    <div key={g.id} className="mini-kluis-regel">
                      <Link naar={`/kluis/${g.id}`} className="mini-kluis-naam">
                        <S.icoon size={15} className="stil" />
                        <span className="afkap">{g.titel}</span>
                        {g.gebruikersnaam && <small className="stil afkap">{g.gebruikersnaam}</small>}
                      </Link>
                      <Code id={g.id} heeft={g.heeftWaarde} klein />
                    </div>
                  )
                })}
                {data.codes.length > 10 && <Link naar="/kluis" className="klein-tekst">En nog {data.codes.length - 10}…</Link>}
              </div>
            ) : <p className="stil">{beheerder ? 'Niets persoonlijk met je gedeeld; de hele kluis staat onder Kluis.' : 'Er is nog niets met je gedeeld. Mis je de wifi of een code van je vestiging? Meld het.'}</p>}
          </section>

          <section className="vlak">
            <div className="vlak-kop">
              <h2><Monitor size={15} /> Mijn spullen</h2>
            </div>
            {data.mijnItems.length ? (
              <div className="mini-lijst">
                {data.mijnItems.map((i) => {
                  const S = itemSoort(i.soort)
                  return (
                    <Link key={i.id} naar={`/i/${i.id}`} className="mini-regel">
                      <S.icoon size={15} className="stil" />
                      <span className="afkap">{i.naam}</span>
                      <small className="stil afkap">{itemKenmerk(i) ?? S.label}</small>
                    </Link>
                  )
                })}
              </div>
            ) : <p className="stil">Er staat niets op jouw naam: geen laptop, sleutel, pas of auto.</p>}
          </section>

          <section className="vlak">
            <div className="vlak-kop">
              <h2><FolderLock size={15} /> Waar ik bij kan</h2>
              <Link naar="/toegang" className="vlak-link">Toegang vragen <ArrowRight size={12} /></Link>
            </div>
            {data.toegang.length ? (
              <div className="mini-lijst">
                {data.toegang.map((a) => (
                  <Link key={a.id} naar={`/s/${a.systeemId}`} className="mini-regel">
                    <FolderLock size={15} className="stil" />
                    <span className="afkap">{a.systeemNaam}</span>
                    <Badge kleur={niveau(a.niveau).kleur}>{niveau(a.niveau).label}</Badge>
                  </Link>
                ))}
              </div>
            ) : <p className="stil">Er staat nog geen toegang op je naam in het register.</p>}
          </section>
        </div>
      )}

      {data && data.links.length > 0 && (
        <section className="vlak">
          <div className="vlak-kop">
            <h2><Link2 size={15} /> Links</h2>
            <Link naar="/links" className="vlak-link">Alle links <ArrowRight size={12} /></Link>
          </div>
          <LinkTegels links={data.links} />
        </section>
      )}
    </div>
  )
}

function AandachtLijst({ regels }: { regels: Aandacht[] }) {
  const naar = (a: Aandacht) => a.soort === 'item' ? `/i/${a.id}` : a.soort === 'geheim' ? `/kluis/${a.id}` : a.soort === 'ticket' ? `/t/${a.id}` : '/toegang'
  return (
    <div className="aandacht-lijst">
      {regels.map((a, n) => (
        <button key={n} type="button" className={`aandacht-regel ernst-${a.ernst}`} onClick={() => navigeer(naar(a))}>
          <span className="punt" />
          <span className="afkap"><b>{a.titel}</b> <span className="stil">{a.wat}</span></span>
          {a.wanneer && <span className="stil aandacht-wanneer">{termijn(a.wanneer)}</span>}
        </button>
      ))}
    </div>
  )
}

/** De links als tegels, per groep. Ook gebruikt op de pagina Links. */
export function LinkTegels({ links, onBewerk }: { links: LinkT[]; onBewerk?: (l: LinkT) => void }) {
  const groepen = new Map<string, LinkT[]>()
  for (const l of links) groepen.set(l.groep, [...(groepen.get(l.groep) ?? []), l])
  return (
    <div className="link-groepen">
      {[...groepen].map(([groep, rijen]) => (
        <div key={groep} className="link-groep">
          <h3>{groep}</h3>
          <div className="link-tegels">
            {rijen.map((l) => (
              <a key={l.id} href={l.url} target="_blank" rel="noopener noreferrer" className="link-tegel"
                onContextMenu={onBewerk ? (e) => { e.preventDefault(); onBewerk(l) } : undefined}>
                <span className="link-letter">{l.titel.slice(0, 1).toUpperCase()}</span>
                <span className="link-tekst">
                  <strong className="afkap">{l.titel}</strong>
                  <small className="afkap">{l.omschrijving ?? kaalAdres(l.url)}</small>
                </span>
                <ExternalLink size={13} className="stil" />
              </a>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}


/* De bewaking: wat elk kwartier wordt nagekeken, storingen bovenaan. */
function BewakingVlak({ regels }: { regels: Bewaking[] }) {
  const storingen = regels.filter((r) => r.status === 'storing').length
  return (
    <section className="vlak">
      <div className="vlak-kop">
        <h2><Radar size={15} /> Bewaking</h2>
        <span className="vlak-link stil">{storingen ? `${storingen} storing${storingen === 1 ? '' : 'en'}` : 'alles bereikbaar'}</span>
      </div>
      <div className="bewaking-lijst">
        {regels.map((r) => (
          <button key={r.sleutel} type="button" className={`bewaking-regel status-${r.status}`}
            onClick={() => navigeer(r.ticketId ? `/t/${r.ticketId}` : r.soort === 'item' ? `/i/${r.onderwerp}` : '/')}>
            <span className="punt" />
            <span className="afkap"><b>{r.naam}</b> <span className="stil">{r.uitleg}</span></span>
            <span className="stil bewaking-wanneer">{r.gemetenAt ? geleden(r.gemetenAt) : ''}</span>
          </button>
        ))}
      </div>
    </section>
  )
}
